import { useAtomValue, useSetAtom, useAtom } from 'jotai'
import { useNavigate } from 'react-router-dom'
import { useState, useRef, useEffect } from 'react'
import * as DashboardStore from '../../stores/DashboardStore'
import { currentUserQueryAtom, SignOutStore } from '../../auth'
import { pageLinks } from '../../pageLinks'

// ============================================================================
// Icons
// ============================================================================

function PlusIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
    </svg>
  )
}

function MoreIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 6.75a.75.75 0 110-1.5.75.75 0 010 1.5zM12 12.75a.75.75 0 110-1.5.75.75 0 010 1.5zM12 18.75a.75.75 0 110-1.5.75.75 0 010 1.5z" />
    </svg>
  )
}

function SparkleIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 16 16" fill="currentColor">
      <path d="M8 0L9.79 6.21L16 8L9.79 9.79L8 16L6.21 9.79L0 8L6.21 6.21L8 0Z" />
    </svg>
  )
}

function LabelIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M9.568 3H5.25A2.25 2.25 0 003 5.25v4.318c0 .597.237 1.17.659 1.591l9.581 9.581c.699.699 1.78.872 2.607.33a18.095 18.095 0 005.223-5.223c.542-.827.369-1.908-.33-2.607L11.16 3.66A2.25 2.25 0 009.568 3z" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M6 6h.008v.008H6V6z" />
    </svg>
  )
}

function LogOutIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 9V5.25A2.25 2.25 0 0013.5 3h-6a2.25 2.25 0 00-2.25 2.25v13.5A2.25 2.25 0 007.5 21h6a2.25 2.25 0 002.25-2.25V15m3 0l3-3m0 0l-3-3m3 3H9" />
    </svg>
  )
}

// ============================================================================
// Helper
// ============================================================================

function formatRelativeTime(dateStr: string): string {
  const date = new Date(dateStr)
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffMins = Math.floor(diffMs / 60000)
  const diffHours = Math.floor(diffMs / 3600000)
  const diffDays = Math.floor(diffMs / 86400000)

  if (diffMins < 1) return 'Just now'
  if (diffMins < 60) return `${diffMins}m ago`
  if (diffHours < 24) return `${diffHours}h ago`
  if (diffDays < 7) return `${diffDays}d ago`
  return date.toLocaleDateString()
}

// ============================================================================
// Components
// ============================================================================

function Header() {
  const { data: user } = useAtomValue(currentUserQueryAtom)
  const signOut = useSetAtom(SignOutStore.signOutAtom)

  return (
    <header className="h-14 px-6 flex items-center justify-between border-b border-[#E5E5E5] bg-white shrink-0">
      <div className="flex items-center gap-2">
        <div className="size-2 rounded-full bg-[#16A34A]" />
        <span className="text-sm font-semibold text-[#18181B]">Vertco</span>
        <SparkleIcon className="size-3.5 text-[#A1A1AA]" />
      </div>
      <div className="flex items-center gap-3">
        {user && (
          <span className="text-sm text-[#52525B]">{user.name || user.email}</span>
        )}
        <button
          onClick={() => signOut()}
          className="flex items-center gap-1.5 px-3 h-8 text-sm text-[#52525B] hover:text-[#18181B] hover:bg-[#F5F5F5] rounded-md transition-colors"
        >
          <LogOutIcon className="size-4" />
          Sign out
        </button>
      </div>
    </header>
  )
}

function EmptyState() {
  const navigate = useNavigate()
  const createLabel = useSetAtom(DashboardStore.createLabelAtom)
  const [creating, setCreating] = useState(false)

  const handleCreate = async () => {
    setCreating(true)
    try {
      const id = await createLabel()
      navigate(pageLinks.LabelGenerator(id))
    } finally {
      setCreating(false)
    }
  }

  return (
    <div className="flex-1 flex items-center justify-center">
      <div className="text-center max-w-sm">
        <div className="mx-auto w-16 h-16 rounded-full bg-[#F5F5F5] flex items-center justify-center mb-4">
          <LabelIcon className="size-8 text-[#A1A1AA]" />
        </div>
        <h2 className="text-lg font-semibold text-[#18181B] mb-2">No labels yet</h2>
        <p className="text-sm text-[#52525B] mb-6">
          Create your first compliance label with AI assistance.
        </p>
        <button
          onClick={handleCreate}
          disabled={creating}
          className="inline-flex items-center gap-2 px-4 h-10 bg-[#18181B] hover:bg-[#27272A] text-white text-sm font-medium rounded-md transition-colors disabled:opacity-50"
        >
          <PlusIcon className="size-4" />
          {creating ? 'Creating...' : 'New Label'}
        </button>
      </div>
    </div>
  )
}

function LabelCardMenu({ labelId, labelName, onClose }: { labelId: string; labelName: string; onClose: () => void }) {
  const menuRef = useRef<HTMLDivElement>(null)
  const [renamingId, setRenamingId] = useAtom(DashboardStore.renamingLabelIdAtom)
  const setRenameInput = useSetAtom(DashboardStore.renameInputAtom)
  const setDeletingId = useSetAtom(DashboardStore.deletingLabelIdAtom)

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        onClose()
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [onClose])

  return (
    <div
      ref={menuRef}
      className="absolute right-2 top-10 z-10 bg-white border border-[#E5E5E5] rounded-lg shadow-lg py-1 min-w-[140px]"
    >
      <button
        className="w-full px-3 py-2 text-left text-sm text-[#18181B] hover:bg-[#F5F5F5] transition-colors"
        onClick={() => {
          setRenamingId(labelId)
          setRenameInput(labelName)
          onClose()
        }}
      >
        Rename
      </button>
      <button
        className="w-full px-3 py-2 text-left text-sm text-[#DC2626] hover:bg-[#FEF2F2] transition-colors"
        onClick={() => {
          setDeletingId(labelId)
          onClose()
        }}
      >
        Delete
      </button>
    </div>
  )
}

function LabelCard({ id, name, thumbnailUrl, updatedAt }: { id: string; name: string; thumbnailUrl: string | null; updatedAt: string }) {
  const navigate = useNavigate()
  const [menuOpen, setMenuOpen] = useState(false)

  return (
    <div className="relative group">
      <button
        onClick={() => navigate(pageLinks.LabelGenerator(id))}
        className="w-full text-left bg-white border border-[#E5E5E5] rounded-lg hover:shadow-sm transition-shadow overflow-hidden"
      >
        {/* Thumbnail area */}
        <div className="h-40 bg-[#FAFAFA] flex items-center justify-center border-b border-[#F0F0F0]">
          {thumbnailUrl ? (
            <img src={thumbnailUrl} alt={name} className="max-h-full max-w-full object-contain p-3" />
          ) : (
            <LabelIcon className="size-10 text-[#E5E5E5]" />
          )}
        </div>
        {/* Info */}
        <div className="p-3">
          <h3 className="text-sm font-medium text-[#18181B] truncate">{name}</h3>
          <p className="text-xs text-[#A1A1AA] mt-1">{formatRelativeTime(updatedAt)}</p>
        </div>
      </button>

      {/* More menu button */}
      <button
        onClick={(e) => {
          e.stopPropagation()
          setMenuOpen(!menuOpen)
        }}
        className="absolute top-2 right-2 size-8 flex items-center justify-center rounded-md bg-white/80 border border-[#E5E5E5] opacity-0 group-hover:opacity-100 transition-opacity hover:bg-[#F5F5F5]"
      >
        <MoreIcon className="size-4 text-[#52525B]" />
      </button>

      {menuOpen && (
        <LabelCardMenu labelId={id} labelName={name} onClose={() => setMenuOpen(false)} />
      )}
    </div>
  )
}

function RenameDialog() {
  const [renamingId, setRenamingId] = useAtom(DashboardStore.renamingLabelIdAtom)
  const [renameInput, setRenameInput] = useAtom(DashboardStore.renameInputAtom)
  const renameLabel = useSetAtom(DashboardStore.renameLabelAtom)
  const [submitting, setSubmitting] = useState(false)

  if (!renamingId) return null

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setSubmitting(true)
    try {
      await renameLabel()
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-xl p-6 w-full max-w-sm shadow-lg">
        <h3 className="text-base font-semibold text-[#18181B] mb-4">Rename label</h3>
        <form onSubmit={handleSubmit}>
          <input
            autoFocus
            value={renameInput}
            onChange={(e) => setRenameInput(e.target.value)}
            className="w-full h-10 px-3 border border-[#E5E5E5] rounded-md text-sm text-[#18181B] focus:border-[#18181B] focus:ring-1 focus:ring-[#18181B] outline-none"
            placeholder="Label name"
          />
          <div className="flex justify-end gap-2 mt-4">
            <button
              type="button"
              onClick={() => setRenamingId(null)}
              className="px-4 h-9 text-sm text-[#52525B] hover:bg-[#F5F5F5] rounded-md transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting || !renameInput.trim()}
              className="px-4 h-9 bg-[#18181B] hover:bg-[#27272A] text-white text-sm font-medium rounded-md transition-colors disabled:opacity-50"
            >
              {submitting ? 'Saving...' : 'Save'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

function DeleteDialog() {
  const [deletingId, setDeletingId] = useAtom(DashboardStore.deletingLabelIdAtom)
  const deleteLabel = useSetAtom(DashboardStore.deleteLabelAtom)
  const [submitting, setSubmitting] = useState(false)

  if (!deletingId) return null

  const handleDelete = async () => {
    setSubmitting(true)
    try {
      await deleteLabel(deletingId)
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-xl p-6 w-full max-w-sm shadow-lg">
        <h3 className="text-base font-semibold text-[#18181B] mb-2">Delete label</h3>
        <p className="text-sm text-[#52525B] mb-4">Are you sure you want to delete this label? This action cannot be undone.</p>
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={() => setDeletingId(null)}
            className="px-4 h-9 text-sm text-[#52525B] hover:bg-[#F5F5F5] rounded-md transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleDelete}
            disabled={submitting}
            className="px-4 h-9 bg-[#DC2626] hover:bg-[#B91C1C] text-white text-sm font-medium rounded-md transition-colors disabled:opacity-50"
          >
            {submitting ? 'Deleting...' : 'Delete'}
          </button>
        </div>
      </div>
    </div>
  )
}

function LabelGrid() {
  const navigate = useNavigate()
  const { data, isPending } = useAtomValue(DashboardStore.refreshAtom)
  const createLabel = useSetAtom(DashboardStore.createLabelAtom)
  const [creating, setCreating] = useState(false)

  const handleCreate = async () => {
    setCreating(true)
    try {
      const id = await createLabel()
      navigate(pageLinks.LabelGenerator(id))
    } finally {
      setCreating(false)
    }
  }

  if (isPending) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="text-sm text-[#A1A1AA]">Loading...</div>
      </div>
    )
  }

  const labels = data?.labels ?? []

  if (labels.length === 0) {
    return <EmptyState />
  }

  return (
    <div className="flex-1 overflow-y-auto">
      <div className="max-w-6xl mx-auto px-6 py-8">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-xl font-semibold text-[#18181B]">My Labels</h1>
          <button
            onClick={handleCreate}
            disabled={creating}
            className="inline-flex items-center gap-2 px-4 h-9 bg-[#18181B] hover:bg-[#27272A] text-white text-sm font-medium rounded-md transition-colors disabled:opacity-50"
          >
            <PlusIcon className="size-4" />
            {creating ? 'Creating...' : 'New Label'}
          </button>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
          {labels.map((label) => (
            <LabelCard
              key={label.id}
              id={label.id}
              name={label.name}
              thumbnailUrl={label.thumbnailUrl}
              updatedAt={label.updatedAt}
            />
          ))}
        </div>
      </div>
    </div>
  )
}

// ============================================================================
// Main Component
// ============================================================================

export default function Dashboard() {
  return (
    <div className="h-screen flex flex-col bg-[#FAFAFA]">
      <Header />
      <LabelGrid />
      <RenameDialog />
      <DeleteDialog />
    </div>
  )
}
